// src/services/organization.service.ts
import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import {
  Organization,
  OrganizationStatus,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  OrganizationStats,
} from '../types/organization.types';

const COLLECTION_NAME = 'organizations';

// Helper to create URL-friendly slug
function createSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}


export async function createOrganization(
  data: CreateOrganizationDto,
  userId: string,
  userName: string
): Promise<Organization> {
  const now = Timestamp.now();
  const slug = createSlug(data.name);

  const orgData = {
    name: data.name,
    slug,
    status: 'active' as OrganizationStatus,
    contactName: data.contactName || null,
    contactEmail: data.contactEmail || null,
    contactPhone: data.contactPhone || null,
    accountingEmails: data.accountingEmails || null,
    address: data.address || null,
    logoUrl: null,
    createdAt: now,
    updatedAt: now,
    createdBy: userId,
    createdByName: userName,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), orgData);

  return {
    id: docRef.id,
    ...orgData,
  } as Organization;
}


export async function getOrganization(id: string): Promise<Organization | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Organization;
}

export async function getAllOrganizations(): Promise<Organization[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Organization[];
}

export async function getActiveOrganizations(): Promise<Organization[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('status', '==', 'active'),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Organization[];
}

export async function getOrganizationBySlug(slug: string): Promise<Organization | null> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('slug', '==', slug)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as Organization;
}


export async function updateOrganization(
  id: string,
  data: UpdateOrganizationDto
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);

  const updateData: Record<string, any> = {
    updatedAt: Timestamp.now(),
  };

  if (data.name !== undefined) {
    updateData.name = data.name;
    updateData.slug = createSlug(data.name);
  }
  if (data.status !== undefined) updateData.status = data.status;
  if (data.contactName !== undefined) updateData.contactName = data.contactName;
  if (data.contactEmail !== undefined) updateData.contactEmail = data.contactEmail;
  if (data.contactPhone !== undefined) updateData.contactPhone = data.contactPhone;
  if (data.accountingEmails !== undefined) updateData.accountingEmails = data.accountingEmails;
  if (data.address !== undefined) updateData.address = data.address;
  if (data.logoUrl !== undefined) updateData.logoUrl = data.logoUrl;

  await updateDoc(docRef, updateData);
}

export async function updateOrganizationStatus(
  id: string,
  status: OrganizationStatus
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    status,
    updatedAt: Timestamp.now(),
  });
}

export async function setPrimaryAdmin(
  organizationId: string,
  userId: string | null,
  userName: string | null
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, organizationId);
  await updateDoc(docRef, {
    primaryAdminId: userId,
    primaryAdminName: userName,
    updatedAt: Timestamp.now(),
  });
}

export async function clearPrimaryAdmin(organizationId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, organizationId);
  await updateDoc(docRef, {
    primaryAdminId: null,
    primaryAdminName: null,
    updatedAt: Timestamp.now(),
  });
}


export async function getOrganizationStats(organizationId: string): Promise<OrganizationStats> {
  // Get user count
  const usersQuery = query(
    collection(db, 'users'),
    where('organizationId', '==', organizationId)
  );
  const usersSnapshot = await getDocs(usersQuery);

  // Get project count
  const projectsQuery = query(
    collection(db, 'projects'),
    where('organizationId', '==', organizationId)
  );
  const projectsSnapshot = await getDocs(projectsQuery);

  // Get active workload count (through projects)
  let activeWorkloadCount = 0;
  let totalLeadCount = 0;

  for (const projectDoc of projectsSnapshot.docs) {
    const projectId = projectDoc.id;

    // Count active workloads
    const workloadsQuery = query(
      collection(db, 'workloads'),
      where('projectId', '==', projectId),
      where('status', '==', 'active')
    );
    const workloadsSnapshot = await getDocs(workloadsQuery);
    activeWorkloadCount += workloadsSnapshot.size;

    // Count leads
    const leadsQuery = query(
      collection(db, 'leads'),
      where('projectId', '==', projectId)
    );
    const leadsSnapshot = await getDocs(leadsQuery);
    totalLeadCount += leadsSnapshot.size;
  }

  return {
    userCount: usersSnapshot.size,
    projectCount: projectsSnapshot.size,
    activeWorkloadCount,
    totalLeadCount,
  };
}

// Export as object for consistent API
export const organizationService = {
  create: createOrganization,
  get: getOrganization,
  getAll: getAllOrganizations,
  getActive: getActiveOrganizations,
  getBySlug: getOrganizationBySlug,
  update: updateOrganization,
  updateStatus: updateOrganizationStatus,
  setPrimaryAdmin: setPrimaryAdmin,
  clearPrimaryAdmin: clearPrimaryAdmin,
  getStats: getOrganizationStats,
};
